/** Composante SectionSparkle de TimTools */
export default class SectionSparkle {
  /**
   * @param {HTMLElement} element - Section sur laquelle la composante est instanciée
   */
  constructor(element) {
    this.element = element;
    this.options = {
      interval: 15,
      duration: 1800,
      spread: 145,
      size: 75,
      maxParticles: 200,
    };
    this.shapes = ['square', 'circle', 'plus'];
    this.colors = ['primary', 'black', 'black-soft', 'primary-soft'];
    this.colorIndex = 0;
    this.particles = new Map();
    this.lastEmission = -Infinity;
    this.lastPosition = null;
    this.motionPreference = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );
    this.onPointerMove = this.spawn.bind(this);
    this.onPointerLeave = this.reset.bind(this);
    this.onMotionChange = this.clear.bind(this);
    this.init();
  }

  init() {
    this.setOptions();
    this.hadClass = this.element.classList.contains('section-sparkle');
    this.element.classList.add('section-sparkle');
    this.backLayer = this.createLayer('back');
    this.layer = this.createLayer('front');
    this.element.addEventListener('pointermove', this.onPointerMove);
    this.element.addEventListener('pointerleave', this.onPointerLeave);
    this.element.addEventListener('pointercancel', this.onPointerLeave);
    this.motionPreference.addEventListener('change', this.onMotionChange);
  }

  createLayer(depth) {
    const layer = document.createElement('div');
    layer.className = `section-sparkle__layer section-sparkle__layer--${depth}`;
    layer.setAttribute('aria-hidden', 'true');
    this.element.append(layer);
    return layer;
  }

  setOptions() {
    const limits = {
      interval: [16, 500],
      duration: [100, 2000],
      spread: [0, 150],
      size: [2, 40],
      maxParticles: [1, 100],
    };
  }

  spawn(event) {
    if (event.pointerType !== 'mouse' || this.motionPreference.matches) return;
    if (this.particles.size >= this.options.maxParticles) return;

    const now = performance.now();
    if (now - this.lastEmission < this.options.interval) return;
    if (
      this.lastPosition &&
      Math.hypot(
        event.clientX - this.lastPosition.x,
        event.clientY - this.lastPosition.y,
      ) < 6
    )
      return;

    // Le calque donne un repère local, même lorsque la page est défilée.
    const rect = this.layer.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x =
      ((event.clientX - rect.left) * this.layer.clientWidth) / rect.width;
    const y =
      ((event.clientY - rect.top) * this.layer.clientHeight) / rect.height;
    this.lastEmission = now;
    this.lastPosition = { x: event.clientX, y: event.clientY };
    this.createParticle(x, y);
  }

  createParticle(x, y) {
    const particle = document.createElement('span');
    const shape = this.shapes[Math.floor(Math.random() * this.shapes.length)];
    const size = this.options.size * (0.6 + Math.random() * 0.8);
    const angle = Math.random() * Math.PI * 2;
    const distance = this.options.spread * (0.5 + Math.random() * 0.5);
    const rotation = Math.random() * 180 - 90;
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance;

    const color = this.colors[this.colorIndex];
    this.colorIndex = (this.colorIndex + 1) % this.colors.length;
    particle.className = `section-sparkle__particle section-sparkle__particle--${shape} section-sparkle__particle--${color}`;
    particle.style.width = `${size}px`;
    particle.style.height = `${size}px`;
    particle.style.left = `${x + Math.cos(angle) * 8}px`;
    particle.style.top = `${y + Math.sin(angle) * 8}px`;
    const layer = Math.random() < 0.5 ? this.backLayer : this.layer;
    layer.append(particle);

    const animation = particle.animate(
      [
        { opacity: 0, transform: 'translate(-50%, -50%) scale(0.3)' },
        { opacity: 0.85, offset: 0.15 },
        {
          opacity: 0,
          transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px) rotate(${rotation}deg) scale(0.2)`,
        },
      ],
      {
        duration: this.options.duration,
        easing: 'ease-out',
        fill: 'forwards',
      },
    );

    this.particles.set(particle, animation);
    animation.onfinish = () => {
      particle.remove();
      this.particles.delete(particle);
    };
  }

  reset() {
    this.lastPosition = null;
    this.lastEmission = -Infinity;
  }

  clear() {
    for (const [particle, animation] of this.particles) {
      animation.cancel();
      particle.remove();
    }
    this.particles.clear();
    this.reset();
  }

  destroy() {
    this.element.removeEventListener('pointermove', this.onPointerMove);
    this.element.removeEventListener('pointerleave', this.onPointerLeave);
    this.element.removeEventListener('pointercancel', this.onPointerLeave);
    this.motionPreference.removeEventListener('change', this.onMotionChange);
    this.clear();
    this.layer.remove();
    this.backLayer.remove();
    if (!this.hadClass) this.element.classList.remove('section-sparkle');
  }
}
